import { ClipboardDocumentIcon, EllipsisVerticalIcon, EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { FolderPlusIcon } from '@heroicons/react/24/outline'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useParams } from 'react-router'
import { EmptyState } from '#/components/empty-state'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { Separator } from '#/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#/components/ui/table'
import { Heading, Subheading, Text } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { chargerAPI } from '#/api/charger'
import { DeleteChargerModal } from './@modal/delete/page'
import type { ChargerSetup } from 'cleverev-types'
import { toast } from 'sonner'


export default function ChargersPage() {
  const { t } = useTranslation([ 'chargers' ])
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ serialNumber: string } | null>(null)

  // Fetch chargers
  const { data: { chargerSetups, totalCount } } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'chargers' ],
    queryFn: () => chargerAPI.listChargerSetups(params.organization),
    select: data => {
      data.chargerSetups.forEach(charger => {
        queryClient.setQueryData<ChargerSetup>(
          [ 'organizations', params.organization, 'chargers', charger.serialNumber ],
          charger
        )
      })

      return data
    }
  })

  // Copy partner id and notify
  function copyChargerId(charger: ChargerSetup) {
    copy(charger.serialNumber)
    toast.info(t('view_charger.id_copied'))
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Outlet />

      <div className="flex w-full items-end justify-between gap-4">
        <Heading>{t('charger_list.page_title')}</Heading>
        <Link to="./new">
          <Button>
            <PlusIcon />
            {t('charger_list.action_new_charger')}
          </Button>
        </Link>
      </div>

      <Text>{t('charger_list.header_description')}</Text>

      <Separator />

      {totalCount === 0 ? (
        <EmptyState
          title={t('charger_list.empty_title')}
          description={t('charger_list.empty_description')}
          Icon={FolderPlusIcon}
        >
          <Link to="./new">
            <Button>
              <PlusIcon />
              {t('charger_list.action_new_charger')}
            </Button>
          </Link>
        </EmptyState>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fields.serialNumber')}</TableHead>
                <TableHead>{t('fields.vendor')}</TableHead>
                <TableHead>{t('fields.model')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isDeleteModalOpen !== null
              && (<DeleteChargerModal
                serialNumber={isDeleteModalOpen.serialNumber}
                onClose={() => setIsDeleteModalOpen(null)}
              />)}
              {chargerSetups.map(charger => (
                <TableRow key={charger.serialNumber}>
                  <TableCell>
                    <Link to={`./${charger.serialNumber}`} state={{ name: charger.serialNumber }}>
                      <Subheading>{charger.serialNumber}</Subheading>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {charger.vendor ?? '-'}
                  </TableCell>
                  <TableCell>
                    {charger.model ?? '-'}
                  </TableCell>
                  <TableCell className="text-right flex flex-row justify-end">
                    <Link to={`./${charger.serialNumber}`} state={{ name: charger.serialNumber }}>
                      <Button variant="ghost" className="mr-4">
                        {t('charger_list.view_button')}
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuLabel>{t('charger_list.menu.label')}</DropdownMenuLabel>
                        <Link to={`./${charger.serialNumber}`} state={{ name: charger.serialNumber }}>
                          <DropdownMenuItem>
                            <EyeIcon className="text-tertiary" />
                            {t('charger_list.menu.view')}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuItem onClick={() => copyChargerId(charger)}>
                          <ClipboardDocumentIcon className="text-tertiary" />
                          {t('charger_list.menu.copy_id')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() =>
                          setIsDeleteModalOpen({
                            serialNumber: charger.serialNumber
                          })}>
                          <TrashIcon className="text-tertiary" />
                          {t('charger_list.menu.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

    </div>
  )
}
